import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, LayoutGrid } from 'lucide-react';
import { tools, ToolCategory } from '@/data/tools';
import MegaMenuCategory from './MegaMenuCategory';

const categories: ToolCategory[] = [
    'Text',
    'Image',
    'Dev',
    'Network',
    'Converters',
    'Media',
    'Calculator',
    'Science',
    'SEO',
    'Utility',
    'PDF',
    'Design',
    'Brain Games',
    'Fun',
    'Discovery',
    'Security',
    'Earth & Space',
    'Explore'
];

export default function MegaMenu() {
    const [isOpen, setIsOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState<ToolCategory>('Text');
    const menuRef = useRef<HTMLDivElement>(null);

    // Close menu when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    // Close menu on route change
    useEffect(() => {
        setIsOpen(false);
    }, []);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleToolClick = () => {
        setIsOpen(false);
    };

    const activeTools = tools.filter((tool) => tool.category === activeCategory);

    return (
        <div ref={menuRef}>
            {/* Trigger Button */}
            <button
                onClick={handleToggle}
                className={`flex items-center gap-2 px-3 py-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg text-sm font-semibold transition-colors ${isOpen ? 'bg-slate-50 text-slate-900 font-bold' : ''
                    }`}
                aria-expanded={isOpen}
                aria-haspopup="true"
            >
                <LayoutGrid size={16} className="text-slate-500" />
                <span>Tools</span>
                <ChevronDown
                    size={14}
                    className={`transition-transform duration-200 text-slate-400 ${isOpen ? 'rotate-180 text-slate-700' : ''}`}
                />
            </button>

            {/* Megamenu Dropdown */}
            {isOpen && (
                <>
                    {/* Backdrop for mobile */}
                    <div className="fixed inset-0 bg-black/20 z-40 lg:hidden" onClick={() => setIsOpen(false)} />

                    {/* Dropdown Content - Positioned relative to Header container */}
                    <div className="absolute left-0 right-0 top-full mt-4 w-full bg-white border border-gray-200 rounded-2xl shadow-2xl z-50 animate-in fade-in slide-in-from-top-2 overflow-hidden mx-auto">
                        <div className="flex min-h-[400px] max-h-[70vh]">
                            {/* Left Sidebar - Categories */}
                            <div className="w-64 flex-shrink-0 bg-gray-50/80 border-r border-gray-100 p-3 overflow-y-auto custom-scrollbar">
                                <div className="space-y-1">
                                    {categories.map((category) => (
                                        <button
                                            key={category}
                                            onClick={() => setActiveCategory(category)}
                                            onMouseEnter={() => setActiveCategory(category)}
                                            className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center justify-between group ${activeCategory === category
                                                ? 'bg-white text-blue-600 shadow-sm ring-1 ring-black/5'
                                                : 'text-gray-600 hover:bg-white/50 hover:text-gray-900'
                                                }`}
                                        >
                                            <span>{category}</span>
                                            {activeCategory === category && (
                                                <div className="w-1.5 h-1.5 rounded-full bg-blue-600" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Right Content - Tools Grid */}
                            <div className="flex-1 p-6 overflow-y-auto bg-white custom-scrollbar">
                                <div className="mb-6 flex items-center justify-between">
                                    <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                                        <span className="text-blue-600">{activeCategory}</span> Tools
                                        <span className="text-xs font-normal text-gray-400 bg-gray-100 px-2 py-1 rounded-full">{activeTools.length}</span>
                                    </h3>
                                </div>
                                <MegaMenuCategory
                                    categoryName={activeCategory}
                                    tools={activeTools}
                                    showHeader={false}
                                    onToolClick={handleToolClick}
                                />
                                {activeTools.length === 0 && (
                                    <div className="h-full flex flex-col items-center justify-center text-gray-400">
                                        <p>No tools found in this category.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
